import * as D from "./dimensions";
import { SLOT_COUNT, TAU } from "./wheelLayout";

// The spin, simulated rather than choreographed.
//
// The ball is a body with a position and a velocity, under gravity, and every
// surface it can touch is a real surface it collides against: the bowl it is
// thrown into, the brass diamonds set in the wall, the frets standing between
// the pockets, the floors of the pockets themselves and the cone in the middle
// of the wheel. The wheel is a second body with its own speed, and — this is
// the part that matters — the head's surfaces are *moving* when the ball meets
// them, so a fret sweeping past hits the ball rather than merely stopping it.
//
// Nobody decides where it lands. The field it comes to rest in is read off the
// wheel afterwards. What is drawn instead is the throw: how hard the ball is
// launched, how fast the wheel is going and where it starts. Those are the
// only random numbers in the game, and they are the same ones a croupier
// varies. Because the flight is chaotic — a hundredth of a unit of extra speed
// changes which diamond is hit, and which diamond is hit changes everything —
// that spread of throws is what makes the result fair. spinFairness.test.ts
// runs several thousand spins and checks that it actually is.

/** The integrator's timestep. Everything below is written to run at this rate. */
export const STEP = 1 / 240;

/**
 * Gravity, in world units per second squared. The wheel is 1.14 units across
 * the rim where a real one is about 0.4 m, so a world unit is roughly 0.35 m
 * and 9.81 m/s² lands here.
 */
const GRAVITY = 28;
/** Air resistance on the ball, per second. Slight — it is a hard little ball. */
const AIR_DRAG = 0.02;

/**
 * What each surface the ball can meet does to it.
 *
 * `curved` marks the ones the ball *runs along* — the bowl, the apron, the
 * cone. Only those get the correction in `resolveContact` that hands back the
 * speed a step of straight-line motion loses to the surface bending away
 * underneath it. A fret is a flat plate, and the lip is there to stop the ball
 * rather than to carry it: handing that speed back on those two turned the
 * corner between them into a machine, each surface returning what the other
 * had just taken, and the ball circled its pocket for ten seconds before the
 * sums finally ran out.
 */
interface Surface {
  bounce: number;
  friction: number;
  curved?: boolean;
}

const BOWL: Surface = { bounce: 0.12, friction: 0.14, curved: true };
const FLOOR: Surface = { bounce: 0.06, friction: 9.0 };
const FRET_SIDE: Surface = { bounce: 0.18, friction: 4.0 };
const FRET_TOP: Surface = { bounce: 0.3, friction: 3.0 };
const CONE: Surface = { bounce: 0.3, friction: 2.2, curved: true };
const DEFLECTOR: Surface = { bounce: 0.45, friction: 1.2 };

/** Below this closing speed a contact counts as resting rather than a bounce. */
const RESTING_SPEED = 0.5;

/**
 * How far outside a surface the ball still counts as touching it.
 *
 * Resolving a contact leaves the ball exactly on the surface, and a test that
 * asks whether it is strictly inside then says no on the very next step. The
 * ball spends its life a hair outside everything, is touched by nothing, and
 * so is slowed by nothing: it coasts in a straight line across the wheel at a
 * constant tenth of a unit a second, taking whole seconds to reach a pocket it
 * should have stopped in. A skin this thin doesn't move anything visibly — the
 * push-out still uses the true penetration — but it keeps a resting ball in
 * contact with what it is resting on.
 */
const SKIN = 0.0025;

/** How the wheel slows: a viscous term plus a little dry friction. */
const WHEEL_VISCOUS = 0.055;
const WHEEL_DRY = 0.045;

/** The ball counts as settled once it has been this slow, relative to the
 *  wheel it is sitting in, for this long. */
const SETTLE_SPEED = 0.12;
const SETTLE_TIME = 0.3;
/** A spin that somehow hasn't settled by now is put out of its misery. */
const MAX_SPIN_SECONDS = 26;

const SLOT_STEP = TAU / SLOT_COUNT;

export interface Ball {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
}

export interface Spin {
  wheelAngle: number;
  wheelOmega: number;
  ball: Ball;
  elapsed: number;
  /** -1 until the ball has come to rest in a pocket. */
  settledSlot: number;
  /** Where the ball sits in the wheel's own frame once it has settled. */
  restRadius: number;
  restAngle: number;
  restY: number;
  settleTimer: number;
  /** Speed of the ball relative to the wheel — what the renderer trails on. */
  relativeSpeed: number;
}

export interface LaunchOptions {
  /** Where the wheel is when the ball is thrown. */
  wheelAngle?: number;
  rng?: () => number;
}

/**
 * A throw. The spread on these three numbers is the whole of the randomness in
 * the game: everything after this is Newton.
 */
export function launch({ wheelAngle = 0, rng = Math.random }: LaunchOptions = {}): Spin {
  // Enough to hold the ball up on the wall of the ball track, and not so much
  // that it rides over the rim: the wall steepens as it goes, so this range of
  // speeds picks out a band of heights near the top of it to ride at, and the
  // ball works its way down the wall as it slows.
  const speed = 8.5 + rng() * 0.8;
  const angle = rng() * TAU;
  // The wheel turns the other way, as at a real table.
  const wheelOmega = -(2.2 + rng() * 1.3);

  const radius = D.BALL_TRACK_RADIUS;
  // Resting *on* the wall, not floating a ball's radius above it: the wall is
  // steep there, so a ball touching it has its centre offset along the
  // surface's normal, which is most of a ball's diameter higher up than the
  // surface itself. Starting it at the wrong height dropped it straight into
  // the diamonds instead of onto the track.
  const y = D.bowlSurfaceY(radius) + D.BALL_RADIUS * Math.sqrt(1 + surfaceSlope(radius) ** 2);
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  return {
    wheelAngle,
    wheelOmega,
    ball: {
      x: radius * cos,
      y,
      z: radius * sin,
      // Tangential, and against the wheel: the wheel's own speed is negative,
      // so the ball is thrown the positive way round.
      vx: -speed * sin,
      vy: 0,
      vz: speed * cos,
    },
    elapsed: 0,
    settledSlot: -1,
    restRadius: 0,
    restAngle: 0,
    restY: 0,
    settleTimer: 0,
    relativeSpeed: speed,
  };
}

/** The gradient of a surface of revolution at radius `r`, by difference. */
function surfaceSlope(r: number, height: (radius: number) => number = D.bowlSurfaceY): number {
  const h = 1e-4;
  return (height(r + h) - height(r - h)) / (2 * h);
}

/** Wraps an angle into 0…2π. */
const wrap = (a: number) => ((a % TAU) + TAU) % TAU;

/** Which field a point in the wheel's own frame sits in. */
export function slotAt(worldAngle: number, wheelAngle: number): number {
  return Math.min(SLOT_COUNT - 1, Math.floor(wrap(worldAngle - wheelAngle) / SLOT_STEP));
}

/**
 * Resolves one contact: pushes the ball out of the surface and changes its
 * velocity, measured against a surface that may itself be moving.
 *
 * `friction` is a rate per second rather than a Coulomb coefficient. That is
 * the honest way to write it for a ball that spends whole seconds in rolling
 * contact: a per-impact coefficient would slow the ball by an amount that
 * depended on the timestep.
 */
function resolveContact(
  ball: Ball,
  nx: number,
  ny: number,
  nz: number,
  penetration: number,
  surface: Surface,
  dt: number,
  surfaceVx = 0,
  surfaceVy = 0,
  surfaceVz = 0,
) {
  if (penetration > 0) {
    ball.x += nx * penetration;
    ball.y += ny * penetration;
    ball.z += nz * penetration;
  }

  let rx = ball.vx - surfaceVx;
  let ry = ball.vy - surfaceVy;
  let rz = ball.vz - surfaceVz;
  const normalSpeed = rx * nx + ry * ny + rz * nz;

  if (normalSpeed < 0) {
    if (-normalSpeed < RESTING_SPEED) {
      // Resting contact — a ball running along the surface rather than
      // striking it. Two different things push it into the surface over a
      // step, and they have to be told apart.
      //
      // One is the surface curving away underneath a step of straight-line
      // motion. A normal force does no work, so that part is turned back into
      // travel along the surface instead of being thrown away; deleting it
      // charged the ball about a tenth of its speed a second in friction that
      // isn't there, which was most of why it never got a lap round the track.
      //
      // The other is gravity, one step's worth of it, and that part must be
      // deleted. Returning it as well is a machine that runs for ever: the
      // ball ends up sliding round its pocket at a fixed speed, gravity
      // feeding it exactly as fast as friction takes it away, and no spin
      // ever finishes.
      // Gravity's share is only what it pushes *along this normal*: on the
      // near-vertical wall of the bowl that is a fraction of it, and charging
      // the ball the whole of it there took a couple of seconds off the ride.
      const gravityShare = GRAVITY * Math.max(0, ny) * dt;
      const curvature = surface.curved ? Math.max(0, -normalSpeed - gravityShare) : 0;
      rx -= nx * normalSpeed;
      ry -= ny * normalSpeed;
      rz -= nz * normalSpeed;
      const slid = Math.hypot(rx, ry, rz);
      if (slid > 1e-9 && curvature > 0) {
        const keepSpeed = Math.hypot(slid, curvature) / slid;
        rx *= keepSpeed;
        ry *= keepSpeed;
        rz *= keepSpeed;
      }
    } else {
      // A real impact, which really does lose energy.
      const impulse = -(1 + surface.bounce) * normalSpeed;
      rx += nx * impulse;
      ry += ny * impulse;
      rz += nz * impulse;
    }
  }

  // Friction acts on whatever is left sliding across the surface.
  const along = rx * nx + ry * ny + rz * nz;
  const tx = rx - nx * along;
  const ty = ry - ny * along;
  const tz = rz - nz * along;
  const keep = Math.exp(-surface.friction * dt);
  ball.vx = surfaceVx + nx * along + tx * keep;
  ball.vy = surfaceVy + ny * along + ty * keep;
  ball.vz = surfaceVz + nz * along + tz * keep;
}

/** Contact with a surface of revolution y = height(r), which the ball is on top of. */
function contactSurface(
  ball: Ball,
  r: number,
  height: (radius: number) => number,
  surface: Surface,
  dt: number,
  omega: number,
) {
  const slope = surfaceSlope(r, height);
  const scale = 1 / Math.sqrt(1 + slope * slope);
  const cos = r > 1e-6 ? ball.x / r : 1;
  const sin = r > 1e-6 ? ball.z / r : 0;
  // Normal to y = f(r), pointing up out of the surface.
  const nx = -slope * cos * scale;
  const ny = scale;
  const nz = -slope * sin * scale;

  const distance = (ball.y - height(r)) * scale;
  if (distance >= D.BALL_RADIUS + SKIN) return;

  resolveContact(
    ball,
    nx,
    ny,
    nz,
    D.BALL_RADIUS - distance,
    surface,
    dt,
    -omega * ball.z,
    0,
    omega * ball.x,
  );
}

/** One tick of the world. `dt` is expected to be STEP. */
export function step(spin: Spin, dt: number) {
  spin.elapsed += dt;

  // The wheel coasts down under its own friction whatever the ball is doing.
  const direction = Math.sign(spin.wheelOmega);
  const slowing = (WHEEL_VISCOUS * Math.abs(spin.wheelOmega) + WHEEL_DRY) * dt;
  spin.wheelOmega =
    Math.abs(spin.wheelOmega) <= slowing ? 0 : spin.wheelOmega - direction * slowing;
  spin.wheelAngle = wrap(spin.wheelAngle + spin.wheelOmega * dt);

  const ball = spin.ball;

  if (spin.settledSlot >= 0) {
    // Sitting in its pocket, carried round by the wheel.
    const a = spin.restAngle + spin.wheelAngle;
    ball.x = spin.restRadius * Math.cos(a);
    ball.z = spin.restRadius * Math.sin(a);
    ball.y = spin.restY;
    ball.vx = -spin.wheelOmega * ball.z;
    ball.vy = 0;
    ball.vz = spin.wheelOmega * ball.x;
    spin.relativeSpeed = 0;
    return;
  }

  // Gravity and air.
  ball.vy -= GRAVITY * dt;
  const keep = Math.exp(-AIR_DRAG * dt);
  ball.vx *= keep;
  ball.vy *= keep;
  ball.vz *= keep;

  ball.x += ball.vx * dt;
  ball.y += ball.vy * dt;
  ball.z += ball.vz * dt;

  const omega = spin.wheelOmega;
  let r = Math.hypot(ball.x, ball.z);

  // --- the bowl, which stands still ---------------------------------------
  // Only once the ball is clear of the lip of the pocket ring. Below that it
  // is down inside a pocket, where the head's own surfaces have it; the bowl
  // reaches under the lip as a surface of revolution, and letting it push on a
  // ball resting in a pocket threw it back out again on every step.
  if (r >= D.R_POCKET_OUT && ball.y > D.POCKET_LIP_Y) {
    contactSurface(ball, r, D.bowlSurfaceY, BOWL, dt, 0);
  }

  // --- the diamonds --------------------------------------------------------
  // Modelled as spheres: the drawn diamonds are little four-sided pyramids,
  // and at this size the difference between rattling off one and off a sphere
  // in the same place is not something the eye can pick out.
  for (const deflector of D.DEFLECTORS) {
    const dx = ball.x - deflector.radius * Math.cos(deflector.angle);
    const dy = ball.y - (deflector.y + D.DEFLECTOR_SIZE * 0.45);
    const dz = ball.z - deflector.radius * Math.sin(deflector.angle);
    const reach = D.BALL_RADIUS + D.DEFLECTOR_SIZE;
    const distance = Math.hypot(dx, dy, dz);
    if (distance < reach && distance > 1e-6) {
      resolveContact(ball, dx / distance, dy / distance, dz / distance, reach - distance, DEFLECTOR, dt);
    }
  }

  // --- the turning head ----------------------------------------------------
  r = Math.hypot(ball.x, ball.z);
  if (r < D.R_POCKET_OUT + D.BALL_RADIUS) {
    if (r < D.R_POCKET_IN) {
      contactSurface(ball, r, D.coneSurfaceY, CONE, dt, omega);
    } else if (ball.y < D.POCKET_FLOOR_Y + D.BALL_RADIUS + SKIN) {
      resolveContact(
        ball,
        0,
        1,
        0,
        D.POCKET_FLOOR_Y + D.BALL_RADIUS - ball.y,
        FLOOR,
        dt,
        -omega * ball.z,
        0,
        omega * ball.x,
      );
    }

    // The lip round the outer edge of the ring, turning with the wheel. A ball
    // low enough to touch it at all is held by it: a lip this much shorter
    // than the ball is one a ball could in principle ride over, but one that
    // has dropped into the pockets never does on a real wheel, and letting it
    // put the ball back out on the bowl is what the fifteen-second spins were.
    if (r > D.R_POCKET_OUT - D.BALL_RADIUS - SKIN && ball.y < D.POCKET_LIP_Y + D.BALL_RADIUS) {
      const cos = ball.x / r;
      const sin = ball.z / r;
      resolveContact(
        ball,
        -cos,
        0,
        -sin,
        r - (D.R_POCKET_OUT - D.BALL_RADIUS),
        FLOOR,
        dt,
        -omega * ball.z,
        0,
        omega * ball.x,
      );
    }

    // The frets. Only the nearest one can be in the way, and it is either
    // under the ball or beside it — whichever it is less far into.
    const worldAngle = Math.atan2(ball.z, ball.x);
    const offset = wrap(worldAngle - spin.wheelAngle);
    const nearest = Math.round(offset / SLOT_STEP);
    const delta = offset - nearest * SLOT_STEP;
    const across = r * Math.sin(delta);
    const sideReach = D.FRET_HALF_WIDTH + D.BALL_RADIUS;
    const topReach = D.FRET_TOP_Y + D.BALL_RADIUS;

    if (
      Math.abs(across) < sideReach + SKIN &&
      ball.y < topReach + SKIN &&
      r > D.R_POCKET_IN - D.BALL_RADIUS
    ) {
      const intoSide = sideReach - Math.abs(across);
      const intoTop = topReach - ball.y;
      const fretAngle = spin.wheelAngle + nearest * SLOT_STEP;
      if (intoTop < intoSide) {
        resolveContact(
          ball,
          0,
          1,
          0,
          intoTop,
          FRET_TOP,
          dt,
          -omega * ball.z,
          0,
          omega * ball.x,
        );
      } else {
        // Push out along the fret's own face, which is sweeping past at the
        // speed of the wheel — this is the kick that scatters the ball.
        const side = across >= 0 ? 1 : -1;
        const nx = -Math.sin(fretAngle) * side;
        const nz = Math.cos(fretAngle) * side;
        resolveContact(
          ball,
          nx,
          0,
          nz,
          intoSide,
          FRET_SIDE,
          dt,
          -omega * ball.z,
          0,
          omega * ball.x,
        );
      }
    }
  }

  // --- has it stopped? -----------------------------------------------------
  r = Math.hypot(ball.x, ball.z);
  const relativeVx = ball.vx + omega * ball.z;
  const relativeVz = ball.vz - omega * ball.x;
  spin.relativeSpeed = Math.hypot(relativeVx, ball.vy, relativeVz);

  const inPocket =
    r > D.R_POCKET_IN &&
    r < D.R_POCKET_OUT &&
    ball.y < D.FRET_TOP_Y &&
    ball.y < D.POCKET_FLOOR_Y + D.BALL_RADIUS * 1.6;

  if (inPocket && spin.relativeSpeed < SETTLE_SPEED) {
    spin.settleTimer += dt;
  } else {
    spin.settleTimer = 0;
  }

  if (spin.settleTimer >= SETTLE_TIME || spin.elapsed > MAX_SPIN_SECONDS) {
    const worldAngle = Math.atan2(ball.z, ball.x);
    spin.settledSlot = slotAt(worldAngle, spin.wheelAngle);
    spin.restRadius = Math.min(Math.max(r, D.R_POCKET_IN + 0.04), D.R_POCKET_OUT - 0.04);
    spin.restAngle = wrap(worldAngle - spin.wheelAngle);
    spin.restY = D.POCKET_FLOOR_Y + D.BALL_RADIUS;
  }
}

/**
 * Runs the world forward by `seconds`, in fixed steps. The leftover is
 * returned so the caller can carry it into the next frame: a simulation whose
 * timestep followed the frame rate would land somewhere else on a slow phone
 * than on a fast laptop, which for a draw among friends is not acceptable.
 */
export function advance(spin: Spin, seconds: number, carry: number): number {
  let time = carry + Math.min(seconds, 0.25);
  while (time >= STEP) {
    step(spin, STEP);
    time -= STEP;
  }
  return time;
}
